import { Inject, Injectable, PLATFORM_ID, signal } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { StorageService } from './storage.service';

export type AppTheme = 'light' | 'dark';

const THEME_KEY = 'theme';

/**
 * The inline script in index.html reads the same unprefixed 'theme' key directly
 * from localStorage (before Angular bootstraps) to set data-theme on <html> and
 * avoid a flash of the wrong theme - keep the key name in sync with that script.
 */
@Injectable({ providedIn: 'root' })
export class ThemeService {
  private readonly isBrowser: boolean;
  private readonly themeSignal = signal<AppTheme>('light');
  readonly theme = this.themeSignal.asReadonly();

  constructor(
    private storage: StorageService,
    @Inject(PLATFORM_ID) platformId: object,
  ) {
    this.isBrowser = isPlatformBrowser(platformId);
    this.themeSignal.set(this.resolveInitialTheme());
    this.applyTheme(this.themeSignal());
  }

  toggleTheme(): void {
    this.setTheme(this.themeSignal() === 'dark' ? 'light' : 'dark');
  }

  setTheme(theme: AppTheme): void {
    this.themeSignal.set(theme);
    this.applyTheme(theme);
    this.storage.setItem(THEME_KEY, theme);
  }

  private resolveInitialTheme(): AppTheme {
    if (!this.isBrowser) return 'light';

    const stored = this.storage.getItem(THEME_KEY);
    if (stored === 'light' || stored === 'dark') return stored;

    return window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }

  private applyTheme(theme: AppTheme): void {
    if (!this.isBrowser) return;
    document.documentElement.setAttribute('data-theme', theme);
  }
}
